import {
    SURVEY_CHANNELS,
    formatAppointmentDateTime,
    formatPatientName,
    getChannelState,
    getRowKey,
} from './surveyFollowupChannel';
import { getRowSurveySelection, getSurveyTitle } from './surveyFollowupSurvey';

const escapeHtml = (value) => {
    if (value == null || value === '') {
        return '';
    }

    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const formatChannelCell = (record, channelKey, selectedChannel) => {
    const state = getChannelState(record, channelKey);

    if (state.disabled) {
        return '—';
    }

    return selectedChannel === channelKey ? 'Yes' : 'No';
};

const buildRecordRows = (records, channelSelections, surveySelections, activeSurveys) =>
    records
        .map((record) => {
            const rowKey = getRowKey(record);
            const selectedChannel = channelSelections[rowKey] ?? null;
            const selectedSurveyId = getRowSurveySelection(surveySelections, rowKey, record, activeSurveys);
            const surveyTitle = selectedSurveyId
                ? getSurveyTitle(selectedSurveyId, activeSurveys)
                : 'No survey available';

            const channelCells = SURVEY_CHANNELS.map(
                (channel) => `<td class="center">${escapeHtml(formatChannelCell(record, channel.key, selectedChannel))}</td>`
            ).join('');

            return `
                <tr>
                    <td>${escapeHtml(formatPatientName(record))}</td>
                    <td>${escapeHtml(surveyTitle)}</td>
                    <td>${escapeHtml(record.email || '—')}</td>
                    <td>${escapeHtml(record.cellPhone || '—')}</td>
                    <td>${escapeHtml(formatAppointmentDateTime(record))}</td>
                    ${channelCells}
                </tr>
            `;
        })
        .join('');

const buildPrintDocument = ({ records, channelSelections, surveySelections, activeSurveys, title, subtitle }) => {
    const rows = buildRecordRows(records, channelSelections, surveySelections, activeSurveys);
    const subtitleHtml = subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : '';
    const channelHeaders = SURVEY_CHANNELS.map((channel) => `<th class="center">${escapeHtml(channel.label)}</th>`).join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
        @page { size: letter landscape; margin: 0.45in; }
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #181c32; background: #fff; }
        h1 { margin: 0 0 0.25rem; font-size: 1.1rem; font-weight: 400; }
        .subtitle { margin: 0 0 0.85rem; font-size: 0.78rem; color: #78829d; }
        table { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
        thead th { text-align: left; padding: 0.45rem 0.5rem; border-bottom: 2px solid #d8dbe5; font-size: 0.68rem; font-weight: 400; text-transform: uppercase; letter-spacing: 0.04em; color: #000; white-space: nowrap; }
        thead th.center, tbody td.center { text-align: center; }
        tbody td { padding: 0.5rem; border-bottom: 1px solid #eceef3; vertical-align: top; word-break: break-word; }
        tbody tr:nth-child(even) { background: #fafbfc; }
        .empty { padding: 2rem 0; text-align: center; color: #78829d; font-size: 0.78rem; }
    </style>
</head>
<body>
    <h1>${escapeHtml(title)}</h1>
    ${subtitleHtml}
    ${
        records.length
            ? `<table>
                <thead>
                    <tr>
                        <th>Patient</th>
                        <th>Survey</th>
                        <th>Email</th>
                        <th>Cell Phone</th>
                        <th>Appointment</th>
                        ${channelHeaders}
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>`
            : '<p class="empty">No survey followups to print.</p>'
    }
</body>
</html>`;
};

const removePrintFrame = (frame) => {
    if (frame?.parentNode) {
        frame.parentNode.removeChild(frame);
    }
};

export const printSurveyFollowupsList = ({
    records,
    channelSelections,
    surveySelections = {},
    activeSurveys = [],
    title,
    subtitle,
}) => {
    const html = buildPrintDocument({
        records,
        channelSelections,
        surveySelections,
        activeSurveys,
        title,
        subtitle,
    });

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Survey followups print preview');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';

    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    const frameDocument = frameWindow?.document;

    if (!frameWindow || !frameDocument) {
        removePrintFrame(iframe);
        return false;
    }

    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();

    const cleanup = () => {
        removePrintFrame(iframe);
    };

    const triggerPrint = () => {
        try {
            frameWindow.focus();
            frameWindow.print();
        } catch (error) {
            console.error(error);
            cleanup();
        }
    };

    frameWindow.addEventListener('afterprint', cleanup, { once: true });
    window.setTimeout(cleanup, 60000);

    if (frameDocument.readyState === 'complete') {
        window.setTimeout(triggerPrint, 150);
    } else {
        iframe.addEventListener('load', () => window.setTimeout(triggerPrint, 150), { once: true });
    }

    return true;
};

export const buildSurveyFollowupsPrintSubtitle = ({
    recordsCount,
    totalElements,
    pageNumber,
    totalPages,
}) => {
    const parts = ['Confirmed appointments from the last 7 days'];

    if (totalPages > 1) {
        parts.push(`Page ${pageNumber + 1} of ${totalPages}`);
    }

    parts.push(`${recordsCount} record${recordsCount === 1 ? '' : 's'} shown`);

    if (totalElements > recordsCount) {
        parts.push(`${totalElements} total`);
    }

    return parts.join(' · ');
};
